import React, { useMemo } from "react";
import { map } from "lodash";
import ContentOverlay from "components/ContentOverlay";
import { useFindingDetails } from "./FindingDetailsProvider";
import { MarkdownFormatter } from "components/MarkupFormatter";
import { CellRenderers } from "utils/tableUtils";
import { FindingDetailsGrid } from "./FindingDetailsGrid";
import { Collapse, Spin } from "antd";
import { PermissionSetMap } from "constants/RBACConstants";
import Vocabulary from "components/Vocabulary";
import { isCustomFinding, canEditCustomField, getCustomFieldFormatter } from "../utils";
import { hasPermission, isSuperOrTenantAdmin } from "utils/authService";

export function FindingDetailsDescription({ designV3 = true, showCustomFields = false }) {
  const { loading, finding, openFindingFieldEditPopup, findingCustomFields, openCustomFindingFieldEditPopup } = useFindingDetails();
  const header = "DETAILS";
  const _isCustomFinding = isCustomFinding(finding?.testType);
  const list = useMemo(
    () => {
      function markdownOverlayRenderer(value) {
        return (
          <div className="m-t-xs">
            <ContentOverlay
              contentClass="d-flex"
              content={
                <MarkdownFormatter markdown={CellRenderers.VALUE_OR_NA(value)} />
              }
              minHeight={designV3 ? 350 : 45}
            />
          </div>
        );
      }

      return [
        {
          label: "Description",
          path: "description",
          formatter: markdownOverlayRenderer,
          containerClassName: "flex-column",
          span: 24,
          editAction: _isCustomFinding
            ? () => openFindingFieldEditPopup("description")
            : undefined,
          actionPermission: PermissionSetMap.UPDATE_FINDINGS,
          valueBold: false,
        },
        {
          label: <Vocabulary name="stepToReproduce" />,
          path: "stepsToReproduce",
          formatter: markdownOverlayRenderer,
          containerClassName: "flex-column",
          span: 24,
          editAction: _isCustomFinding
            ? () => openFindingFieldEditPopup("stepsToReproduce")
            : undefined,
          actionPermission: PermissionSetMap.UPDATE_FINDINGS,
          valueBold: false,
        },
        {
          label: <Vocabulary name="mitigation" />,
          path: "mitigation",
          formatter: markdownOverlayRenderer,
          containerClassName: "flex-column",
          span: 24,
          editAction: _isCustomFinding
            ? () => openFindingFieldEditPopup("mitigation")
            : undefined,
          actionPermission: PermissionSetMap.UPDATE_FINDINGS,
          valueBold: false,
        },
        {
          label: <Vocabulary name="impact" />,
          path: "impact",
          formatter: markdownOverlayRenderer,
          containerClassName: "flex-column",
          span: 24,
          editAction: _isCustomFinding
            ? () => openFindingFieldEditPopup("impact")
            : undefined,
          actionPermission: PermissionSetMap.UPDATE_FINDINGS,
          valueBold: false,
        },
        // Custom fields - only shown when showCustomFields is true (FF disabled)
        ...(showCustomFields && findingCustomFields?.length > 0 ? (() => {
          const hasEditPermission = isSuperOrTenantAdmin() || hasPermission(PermissionSetMap.EDIT_CUSTOM_FIELDS);
          return map(findingCustomFields, (customField) => {
            const isFieldEditable = canEditCustomField(customField);
            return {
              label: customField.label,
              path: customField.id,
              disableEdit: () => !hasEditPermission || !isFieldEditable,
              editButtonToolTip: !hasEditPermission ? 'No access' : 'Due to a data type conversion, this custom field is currently uneditable. For any modifications, please refer to Global Settings',
              formatter: getCustomFieldFormatter(customField, designV3 ? 350 : 45),
              containerClassName: "flex-column",
              span: 24,
              valueBold: false,
              editAction: () => openCustomFindingFieldEditPopup(customField),
            };
          });
        })() : []),
      ]
    },
    [_isCustomFinding, designV3, openFindingFieldEditPopup, showCustomFields, findingCustomFields, openCustomFindingFieldEditPopup]
  );

  const DetailGrid = (
    <Spin spinning={loading}>
      <FindingDetailsGrid list={list} data={finding} itemGapClassName="m-b-m" />
    </Spin>
  );
  return (
    <div className="finding-description-section">
      {designV3 ? (
        DetailGrid
      ) : (
        <Collapse
          className="collapse-v2"
          defaultActiveKey="details"
          destroyInactivePanel
        >
          <Collapse.Panel header={header} key="details">
            {DetailGrid}
          </Collapse.Panel>
        </Collapse>
      )}
    </div>
  );
}
